import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ChartBar,
  Users,
  BookOpen,
  Spinner
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminReports() {
  const [courseReports, setCourseReports] = useState([]);
  const [userReports, setUserReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/by-course`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/by-user`, { withCredentials: true })
      ]);
      setCourseReports(coursesRes.data);
      setUserReports(usersRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-reports">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Relatórios</h1>
        <p className="text-[#5C6B7A] mb-8">Análises e insights detalhados</p>

        <Tabs defaultValue="courses" className="space-y-6">
          <TabsList>
            <TabsTrigger value="courses" className="flex items-center gap-2">
              <BookOpen size={18} />
              Por Curso
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users size={18} />
              Por Usuário
            </TabsTrigger>
          </TabsList>

          <TabsContent value="courses">
            <Card className="border-[#2C3E50]/10 mb-6">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1A252F]">Taxa de Conclusão por Curso</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={courseReports}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#BDC3C7" />
                      <XAxis 
                        dataKey="course_title" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                      />
                      <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} unit="%" />
                      <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                      <Bar dataKey="completion_rate" fill="#27AE60" radius={[4, 4, 0, 0]} name="Taxa de Conclusao" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#2C3E50]/10">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1A252F]">Detalhes dos Cursos</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F8F9FA]">
                      <TableHead className="font-semibold text-[#1A252F]">Curso</TableHead>
                      <TableHead className="font-semibold text-[#1A252F] text-right">Matrículas</TableHead>
                      <TableHead className="font-semibold text-[#1A252F] text-right">Conclusões</TableHead>
                      <TableHead className="font-semibold text-[#1A252F] text-right">Taxa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {courseReports.map((course) => (
                      <TableRow key={course.course_id}>
                        <TableCell className="font-medium text-[#1A252F]">{course.course_title}</TableCell>
                        <TableCell className="text-right text-[#5C6B7A]">{course.enrollments}</TableCell>
                        <TableCell className="text-right text-[#5C6B7A]">{course.completions}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-medium ${
                            course.completion_rate >= 70 ? 'text-[#27AE60]' : 
                            course.completion_rate >= 40 ? 'text-[#F39C12]' : 
                            'text-[#E74C3C]'
                          }`}>
                            {course.completion_rate.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {courseReports.length === 0 && (
                  <div className="py-12 text-center text-[#5C6B7A]">
                    Nenhum dado de curso disponível
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-[#2C3E50]/10">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1A252F]">User Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F8F9FA]">
                      <TableHead className="font-semibold text-[#1A252F]">User</TableHead>
                      <TableHead className="font-semibold text-[#1A252F]">Email</TableHead>
                      <TableHead className="font-semibold text-[#1A252F] text-right">Enrolled</TableHead>
                      <TableHead className="font-semibold text-[#1A252F] text-right">Completed</TableHead>
                      <TableHead className="font-semibold text-[#1A252F] text-right">Avg Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userReports.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell className="font-medium text-[#1A252F]">{user.user_name}</TableCell>
                        <TableCell className="text-[#5C6B7A]">{user.user_email}</TableCell>
                        <TableCell className="text-right text-[#5C6B7A]">{user.enrollments}</TableCell>
                        <TableCell className="text-right text-[#5C6B7A]">{user.completions}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-medium ${
                            user.average_quiz_score >= 80 ? 'text-[#27AE60]' : 
                            user.average_quiz_score >= 60 ? 'text-[#F39C12]' : 
                            'text-[#5C6B7A]'
                          }`}>
                            {user.average_quiz_score > 0 ? `${user.average_quiz_score}%` : '-'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {userReports.length === 0 && (
                  <div className="py-12 text-center text-[#5C6B7A]">
                    Nenhum dado de usuário disponível
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

export default AdminReports;
